import { useCallback, useContext, useEffect, useState } from "react";
import Image from "next/image";
import { useAtom, useSetAtom } from "jotai";
import axios from "axios";
import React from "react";
import clsx from "clsx";

import { AuthContext } from "@/contexts/auth";
import { CREW_END_POINTS } from "@/constants/crew/endPoints";
import closeModalIcon from "@/assets/icons/close-modal.svg";
import { Photos } from "@/types/common/photos";
import { photosAtom } from "@/states/photosAtom";
import { ReportContext } from "@/contexts/report";
import { showKeepRotateDegreeModalAtom } from "@/states/common/showKeepRotateDegreeModalAtom";
import { clickCancel } from "@/utils/clickCancel";
import { rotateDegreeAtom } from "@/states/rotateDegreeAtom";

import Modal from "../modal";

import RotateButton from "./rotateButton";
import MainSlider from "./mainSlider";
import BottomSlider from "./bottomSlider";
import SaveButton from "./saveButton";

type Props = {
  show: boolean;
  canRotate?: boolean;
  setShow: (show: any) => void;
  showClose?: boolean;
  media: Photos[] | undefined;
  mediaIndex: number;
  setMediaIndex: (index: number) => void;
  text?: string;
  isZendesk?: boolean;
};

const MediaModal: React.FC<Props> = ({
  show,
  canRotate,
  setShow,
  showClose = true,
  media,
  mediaIndex,
  setMediaIndex,
  isZendesk = false,
  text = ""
}: Props) => {
  const [rotateDegrees, setRotateDegrees] = useAtom(rotateDegreeAtom);

  const [rotateAnimation, setRotateAnimation] = useState(false);

  const { token } = useContext(AuthContext);
  const setPhotos = useSetAtom(photosAtom);

  const setShowKeepRotateDegreeModal = useSetAtom(
    showKeepRotateDegreeModalAtom
  ); // 写真回転未保存確認モーダル表示/非表示

  const {
    setFinalPhotos,
    setFinalProblemPhotos,
    setFinalCommentPhotos,
    setOtherReportPhotos
  } = useContext(ReportContext);

  const hasRotateDegree = rotateDegrees.some((degree) => degree !== 0);

  // モーダル表示時にスクロール禁止
  useEffect(() => {
    document.body.style.overflow = show ? "hidden" : "auto";
  }, [show]);

  // ESCキーでモーダルを閉じる
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setShow(false);
        e.preventDefault();
      }
    };

    window.addEventListener("keydown", onKeyDown, false);
    return () => {
      window.removeEventListener("keydown", onKeyDown, false);
    };
  }, []);

  /** スライダーの遷移処理 */
  const goToSlide = useCallback(
    (index: number) => {
      if (!media) return;
      if (index === -1) {
        setMediaIndex(media.length - 1);
      } else if (index === media.length) {
        setMediaIndex(0);
      } else {
        setMediaIndex(index);
      }
    },
    [media, mediaIndex]
  );

  // 左右キーでスライド移動
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") {
        goToSlide(mediaIndex + 1);
      } else if (e.key === "ArrowLeft") {
        goToSlide(mediaIndex - 1);
      }
    };

    window.addEventListener("keydown", onKeyDown, false);
    return () => {
      window.removeEventListener("keydown", onKeyDown, false);
    };
  }, [mediaIndex]);

  // 画像のidと回転角度をpostする関数
  const saveMediaRotateDegrees = async (targetPhotos: Photos[] | undefined) => {
    if (!targetPhotos) return;

    const postData: any = targetPhotos.map((photo, index) => {
      return {
        id: photo.id,
        degree: rotateDegrees[index]
      };
    }); // pathがあるものの写真IDと回転角度をセット

    await axios
      .post(
        `${CREW_END_POINTS.REPORT.ROTATE_PHOTOS}`,
        { photos: postData },
        token
      )
      // postが完了したらrotateDegreeを初期値に戻す
      .then((res) => {
        // photosのphotoGroupが一致していて、さらにphotoのidが一致するものだけをres.data[0]で更新する
        const updatedPhotoDataArr = res.data;

        // 全ての更新された写真データを使用して状態を更新
        const updatePhotosState = (prevPhotos: any) => {
          return prevPhotos.map((photoGroup: any) => ({
            ...photoGroup,
            photos: photoGroup.photos?.map((photo: Photos) => {
              const updatedPhotoData = updatedPhotoDataArr.find(
                (uPhoto: Photos) => uPhoto.id === photo.id
              );
              return updatedPhotoData
                ? { ...photo, ...updatedPhotoData }
                : photo;
            })
          }));
        };

        setPhotos(updatePhotosState);
        setFinalCommentPhotos(updatePhotosState);
        setFinalProblemPhotos(updatePhotosState);
        setOtherReportPhotos(updatePhotosState);
        setFinalPhotos(updatePhotosState);
      })
      .catch((err) => {
        console.error("postに失敗しました", err);
      });
    // 現在の回転角度を初期値に戻す
    // [0, -90, -180, -270, -180]のように複数配列を持っているので全て0に戻す
    setRotateDegrees((prev) => {
      return prev.map(() => 0);
    });
  };

  // ボタンクリックで発火するrotateDegree更新用関数
  // 0, -90, -180, -270の4パターン
  const handleRotateDegrees = (photoIndex: number) => {
    setRotateDegrees((prev) =>
      prev.map((degree, index) => (index === photoIndex ? degree - 90 : degree))
    );
  };

  // 画像回転させる時間を制御するための関数
  const handleRotateAnimation = () => {
    setRotateAnimation(true);
    setTimeout(() => {
      setRotateAnimation(false);
    }, 300);
  };

  return (
    <Modal show={show} title={""} showClose={true} setShow={setShow}>
      <div
        className="fixed left-0 top-0 z-[100] flex h-full w-full flex-col content-between items-center justify-center bg-[rgba(36,36,36,0.9)]	"
        onClick={() => {
          // 角度保存前に消そうとした時は確認用のモーダルを出す
          if (hasRotateDegree) {
            setShowKeepRotateDegreeModal(true);
            return;
          }
          if (showClose) {
            setShow(false);
          }
        }}
      >
        {showClose && (
          <div
            className="absolute right-8 top-8 z-[110] grid h-[2.6rem] w-[2.6rem] place-items-center hover:cursor-pointer hover:opacity-80"
            onClick={(e) => {
              clickCancel(e);

              // 角度保存前に消そうとした時は確認用のモーダルを出す
              if (hasRotateDegree) {
                setShowKeepRotateDegreeModal(true);
                return;
              }
              setShow(false);
            }}
          >
            <Image src={closeModalIcon} alt="閉じる" width={18} height={18} />
          </div>
        )}
        <div
          className="relative w-[calc(100vw-2rem)] rounded-[1.6rem]"
          onClick={(e) => clickCancel(e)}
        >
          <div className="mx-auto min-h-[calc(100vh_-_70px)]">
            <div
              className={clsx(
                canRotate
                  ? "h-[calc(100vh_-_300px)]"
                  : "h-[calc(100vh_-_200px)]",
                "relative w-full"
              )}
            >
              <div className="expandable absolute w-full">
                <MainSlider
                  media={media}
                  mediaIndex={mediaIndex}
                  canRotate={canRotate}
                  rotateDegree={rotateDegrees}
                  setRotateDegree={setRotateDegrees}
                  goToSlide={goToSlide}
                />
                <div
                  className={clsx(
                    canRotate ? "hidden" : "",
                    "mt-[1rem] flex justify-center px-8 text-[1.4rem] text-white"
                  )}
                >
                  <div className="line-clamp-3 max-w-[48rem]">{text}</div>
                </div>
              </div>
            </div>

            {canRotate && (
              <div className="absolute bottom-[17rem] right-[50%] z-[110] h-[2.8rem] w-[2.8rem] translate-x-1/2">
                <RotateButton
                  rotateAnimation={rotateAnimation}
                  handleRotateDegrees={handleRotateDegrees}
                  handleRotateAnimation={handleRotateAnimation}
                  mediaIndex={mediaIndex}
                />
              </div>
            )}

            {/* BottomSlider */}
            <div
              className={clsx(
                "absolute -mx-4 flex h-[10h] w-[100vw] items-center justify-center",
                canRotate ? "bottom-[9rem]" : "bottom-[5em]"
              )}
            >
              {media && media.length > 1 && (
                <BottomSlider
                  media={media}
                  mediaIndex={mediaIndex}
                  goToSlide={goToSlide}
                  isZendesk={isZendesk}
                />
              )}
            </div>
            <div className="absolute bottom-[3rem] w-full">
              {/* 保存ボタン */}
              {canRotate && (
                <SaveButton
                  media={media}
                  hasRotateDegree={hasRotateDegree}
                  handleRotate={saveMediaRotateDegrees}
                  closeModal={() => setShow(false)}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default MediaModal;
